import React, { useEffect, useLayoutEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { flushSync } from 'react-dom';

import { createSuspenseRoute, SECURE_CONFIG } from 'utils/SecureRoute';
import { useInjectSaga } from 'utils/common/injectSaga';
import { useInjectReducer } from 'utils/common/injectedReducers';
import { useUpdateRouterParams } from 'hooks/router';
import { productFullViewKeys } from 'hooks';

import productFullViewReducer from './controllers/reducer';
import productFullViewSaga from './controllers/saga';
import * as globalActions from '@redux/global/actions';
import * as productFullViewActions from 'pages/product-full-view/controllers/actions';

import { PRODUCT_VIEW } from 'static/Constants';
import './style.less';

const { lazy } = React;

const ProductOverviewComponent = lazy(
  () => import('./view/ProductOverviewView')
);

const ProductDetailComponent = lazy(() => import('./view/ProductDetailView'));

const ProductHierarchyComponent = lazy(
  () => import('./view/ProductHierarchyView')
);

const ProductHistory = lazy(() => import('pages/product-history'));
const ProductSubscription = lazy(
  () => import('pages/product-full-view/components/subscription/Subscription')
);

export const productDetailRoutes = (queryClient) => {
  return [
    {
      index: true,
      path: ':id',
      element: createSuspenseRoute({
        secureConfig: SECURE_CONFIG.PRODUCT,
        Component: ProductOverviewComponent,
      }),
    },
    {
      path: `${PRODUCT_VIEW.DETAILVIEW}/:id`,
      element: createSuspenseRoute({
        secureConfig: SECURE_CONFIG.PRODUCT,
        Component: ProductDetailComponent,
      }),
    },
    {
      path: `${PRODUCT_VIEW.HIERARCHY}/:id`,
      element: createSuspenseRoute({
        secureConfig: SECURE_CONFIG.PRODUCT,
        Component: ProductHierarchyComponent,
      }),
    },
    {
      path: 'subscription/:id',
      element: createSuspenseRoute({
        secureConfig: SECURE_CONFIG.PRODUCT,
        Component: ProductSubscription,
      }),
    },
    {
      path: 'versions/:id',
      element: createSuspenseRoute({
        secureConfig: SECURE_CONFIG.PRODUCT,
        Component: ProductHistory,
      }),
    },
  ];
};

const key = 'productFullView';

const ProductFullView = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const queryClient = useQueryClient();

  useInjectReducer({ key, reducer: productFullViewReducer });
  useInjectSaga({ key, saga: productFullViewSaga });
  useUpdateRouterParams();

  useLayoutEffect(() => {
    flushSync(() => {
      dispatch(globalActions.hideSearchBar());
    });
    queryClient.invalidateQueries({ queryKey: ['pim'] });
    queryClient.invalidateQueries({
      queryKey: productFullViewKeys.all,
    });
  }, [dispatch, queryClient]);

  useEffect(() => {
    if (id) {
      sessionStorage.setItem('productId', id);
    }
  }, [id]);

  useEffect(() => {
    return () => {
      dispatch(
        productFullViewActions.updateActiveContent(PRODUCT_VIEW.OVERVIEW)
      );
      dispatch(productFullViewActions.setCurrentHierarchy(null));
      dispatch(productFullViewActions.resetProductImagery());
    };
  }, [dispatch]);

  return <Outlet />;
};

export default ProductFullView;
